import { type FleetConflictDto, type MeDto, UNAVAILABILITY_REASONS, type UnavailabilityDto } from '@bqcrm/shared';
import { Anchor, Box, Group, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconCar } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../api';
import { can, canModule } from '../../auth';
import { dayRange } from '../../format';

interface FleetCalendarDto {
  vehicles: { id: string; plate: string; label: string }[];
  items: UnavailabilityDto[];
  conflicts: FleetConflictDto[];
}

/**
 * Kalendarz (widok tygodnia): pasek „Pojazdy” nad wierszami pracowników — okresy niedostępności aut
 * i kolizje z pracami ekipy (ikona ostrzeżenia). Tylko pojazdy niedostępne w pokazanym tygodniu.
 */
export function FleetCalendarRows({ me, from, to, days, today, cols }: { me: MeDto; from: string; to: string; days: string[]; today: string; cols: string }) {
  const enabled = me.features.flota && (can(me, 'flota.dostepnosc') || can(me, 'flota.podglad'));
  const q = useQuery({ queryKey: ['fleet', 'calendar', from, to], queryFn: () => api<FleetCalendarDto>(`/fleet/calendar?from=${from}&to=${to}`), enabled, retry: false });
  if (!enabled || !q.data) return null;
  const items = q.data.items.filter((u) => !u.cancelledAt && u.dateFrom <= to && u.dateTo >= from);
  const vehicles = q.data.vehicles.filter((v) => items.some((u) => u.vehicleId === v.id));
  const link = canModule(me, 'flota');
  return (
    <Box style={{ borderTop: '1px solid var(--app-line)', background: 'var(--app-subtle)', minWidth: 1000 }}>
      <Group gap={6} px={10} py={4}>
        <IconCar size={14} />
        <Text size="xs" fw={700} tt="uppercase" c="dimmed">
          Pojazdy
        </Text>
        {!vehicles.length && (
          <Text size="xs" c="dimmed">
            — wszystkie dostępne w tym okresie
          </Text>
        )}
      </Group>
      {vehicles.map((v) => (
        <Box key={v.id} style={{ display: 'grid', gridTemplateColumns: cols, minWidth: 1000, borderTop: '1px solid var(--app-line)' }}>
          <Box px={10} py={6} style={{ minWidth: 0 }}>
            {link ? (
              <Anchor component={Link} to={`/flota/pojazdy/${v.id}?zakladka=dostepnosc`} size="sm" fw={600}>
                {v.plate}
              </Anchor>
            ) : (
              <Text size="sm" fw={600}>
                {v.plate}
              </Text>
            )}
            <Text size="xs" c="dimmed" truncate>
              {v.label}
            </Text>
          </Box>
          {days.map((d) => {
            const u = items.find((x) => x.vehicleId === v.id && x.dateFrom <= d && x.dateTo >= d);
            const conflicts = q.data!.conflicts.filter((c) => c.vehicleId === v.id && c.day === d);
            return (
              <Box key={d} p={3} style={{ borderLeft: '1px solid var(--app-line)', background: d === today ? 'var(--app-today)' : undefined }}>
                {u && (
                  <Tooltip
                    multiline
                    w={280}
                    label={[`${UNAVAILABILITY_REASONS[u.reason]} ${dayRange(u.dateFrom, u.dateTo)}`, u.note, u.replacement ? `auto zastępcze: ${u.replacement.plate}` : null, ...conflicts.map((c) => c.text), conflicts.length ? 'Prace nie są przesuwane automatycznie.' : null].filter(Boolean).join('\n')}
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    <Group gap={4} wrap="nowrap" px={6} py={2} style={{ borderRadius: 5, background: 'var(--mantine-color-red-light)', color: 'var(--mantine-color-red-light-color)' }}>
                      {conflicts.length > 0 && <IconAlertTriangle size={14} style={{ flexShrink: 0 }} />}
                      <Text size="xs" fw={600} truncate>
                        {UNAVAILABILITY_REASONS[u.reason]}
                        {conflicts.length > 0 ? ` · kolizja (${conflicts.length})` : ''}
                      </Text>
                    </Group>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
